import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  BookOpen, Plus, Edit, Trash2, Video, FileText, HelpCircle,
  ChevronDown, ChevronRight, Eye, EyeOff, GripVertical,
  PlayCircle, Clock, IndianRupee, Users, TrendingUp, RefreshCw,
  Layers, FolderOpen, FileVideo
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LMSManagement = () => {
  const [activeTab, setActiveTab] = useState('courses');
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  
  // Course Dialog
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    title: '', description: '', thumbnail_url: '', instructor_name: 'Iqra Skills',
    is_free: false, price: '', discount_price: '', category: 'General', tags: ''
  });
  
  // Course Builder State
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedChapters, setExpandedChapters] = useState({});
  
  // Module Dialog
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [moduleForm, setModuleForm] = useState({ title: '', description: '', order: 0 });
  
  // Chapter Dialog
  const [chapterDialogOpen, setChapterDialogOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [chapterForm, setChapterForm] = useState({ title: '', description: '', order: 0 });
  
  // Lesson Dialog
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [lessonForm, setLessonForm] = useState({
    title: '', description: '', order: 0, video_type: 'youtube',
    video_url: '', video_duration: 0, notes_text: '', notes_pdf_url: '', is_preview: false
  });
  
  // Quiz Dialog
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [quizForm, setQuizForm] = useState({
    passing_score: 60,
    questions: [{ question: '', options: ['', '', '', ''], correct_answer: 0, points: 10 }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/lms/courses`),
        axios.get(`${API}/lms/admin/analytics`).catch(() => ({ data: null }))
      ]);
      setCourses(coursesRes.data || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch LMS data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Course handlers
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...courseForm,
        price: parseFloat(courseForm.price) || 0,
        discount_price: courseForm.discount_price ? parseFloat(courseForm.discount_price) : null,
        tags: courseForm.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      if (editingCourse) {
        await axios.put(`${API}/lms/courses/${editingCourse.id}`, data);
        toast.success('Course updated');
      } else {
        await axios.post(`${API}/lms/courses`, data);
        toast.success('Course created');
      }
      setCourseDialogOpen(false);
      resetCourseForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    }
  };

  const resetCourseForm = () => {
    setEditingCourse(null);
    setCourseForm({
      title: '', description: '', thumbnail_url: '', instructor_name: 'Iqra Skills',
      is_free: false, price: '', discount_price: '', category: 'General', tags: ''
    });
  };

  const openEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title,
      description: course.description || '',
      thumbnail_url: course.thumbnail_url || '',
      instructor_name: course.instructor_name || 'Iqra Skills',
      is_free: course.is_free || false,
      price: course.price?.toString() || '',
      discount_price: course.discount_price?.toString() || '',
      category: course.category || 'General',
      tags: (course.tags || []).join(', ')
    });
    setCourseDialogOpen(true);
  };

  const togglePublish = async (courseId) => {
    try {
      const res = await axios.put(`${API}/lms/courses/${courseId}/publish`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle publish status');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await axios.delete(`${API}/lms/courses/${courseId}`);
      toast.success('Course deleted');
      fetchData();
      if (selectedCourse?.id === courseId) setSelectedCourse(null);
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  // Module handlers
  const handleAddModule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lms/courses/${selectedCourse.id}/modules`, moduleForm);
      toast.success('Module added');
      setModuleDialogOpen(false);
      setModuleForm({ title: '', description: '', order: 0 });
      refreshSelectedCourse();
    } catch (error) {
      toast.error('Failed to add module');
    }
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and all its contents?')) return;
    try {
      await axios.delete(`${API}/lms/courses/${selectedCourse.id}/modules/${moduleId}`);
      toast.success('Module deleted');
      refreshSelectedCourse();
    } catch (error) {
      toast.error('Failed to delete module');
    }
  };

  // Chapter handlers
  const openChapterDialog = (moduleId) => {
    setSelectedModuleId(moduleId);
    setChapterDialogOpen(true);
  };

  const handleAddChapter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lms/courses/${selectedCourse.id}/modules/${selectedModuleId}/chapters`, chapterForm);
      toast.success('Chapter added');
      setChapterDialogOpen(false);
      setChapterForm({ title: '', description: '', order: 0 });
      refreshSelectedCourse();
    } catch (error) {
      toast.error('Failed to add chapter');
    }
  };

  // Lesson handlers
  const openLessonDialog = (moduleId, chapterId) => {
    setSelectedModuleId(moduleId);
    setSelectedChapterId(chapterId);
    setLessonDialogOpen(true);
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/lms/courses/${selectedCourse.id}/modules/${selectedModuleId}/chapters/${selectedChapterId}/lessons`,
        { ...lessonForm, video_duration: parseInt(lessonForm.video_duration) || 0 }
      );
      toast.success('Lesson added');
      setLessonDialogOpen(false);
      setLessonForm({
        title: '', description: '', order: 0, video_type: 'youtube',
        video_url: '', video_duration: 0, notes_text: '', notes_pdf_url: '', is_preview: false
      });
      refreshSelectedCourse();
    } catch (error) {
      toast.error('Failed to add lesson');
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;
    try {
      await axios.delete(`${API}/lms/courses/${selectedCourse.id}/lessons/${lessonId}`);
      toast.success('Lesson deleted');
      refreshSelectedCourse();
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  // Quiz handlers
  const openQuizDialog = (lessonId) => {
    setSelectedLessonId(lessonId);
    setQuizDialogOpen(true);
  };

  const addQuizQuestion = () => {
    setQuizForm({
      ...quizForm,
      questions: [...quizForm.questions, { question: '', options: ['', '', '', ''], correct_answer: 0, points: 10 }]
    });
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...quizForm.questions];
    newQuestions[index][field] = value;
    setQuizForm({ ...quizForm, questions: newQuestions });
  };

  const updateOption = (qIndex, oIndex, value) => {
    const newQuestions = [...quizForm.questions];
    newQuestions[qIndex].options[oIndex] = value;
    setQuizForm({ ...quizForm, questions: newQuestions });
  };

  const handleAddQuiz = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lms/courses/${selectedCourse.id}/lessons/${selectedLessonId}/quiz`, quizForm);
      toast.success('Quiz added');
      setQuizDialogOpen(false);
      setQuizForm({
        passing_score: 60,
        questions: [{ question: '', options: ['', '', '', ''], correct_answer: 0, points: 10 }]
      });
      refreshSelectedCourse();
    } catch (error) {
      toast.error('Failed to add quiz');
    }
  };

  const refreshSelectedCourse = async () => {
    if (!selectedCourse) return;
    try {
      const res = await axios.get(`${API}/lms/courses/${selectedCourse.id}`);
      setSelectedCourse(res.data);
      fetchData();
    } catch (error) {
      console.error('Failed to refresh course');
    }
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-purple-600" />
          LMS - Learning Management System
        </h2>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Total Courses</p>
                <p className="text-2xl font-bold">{analytics.total_courses}</p>
                <p className="text-xs text-purple-200">{analytics.published_courses} published</p>
              </div>
              <BookOpen className="h-8 w-8 text-purple-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Enrollments</p>
                <p className="text-2xl font-bold">{analytics.total_enrollments}</p>
                <p className="text-xs text-blue-200">{analytics.completed_enrollments} completed</p>
              </div>
              <Users className="h-8 w-8 text-blue-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Revenue</p>
                <p className="text-2xl font-bold">₹{analytics.total_revenue?.toLocaleString()}</p>
              </div>
              <IndianRupee className="h-8 w-8 text-emerald-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Completion Rate</p>
                <p className="text-2xl font-bold">{analytics.completion_rate?.toFixed(1)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-amber-200" />
            </div>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="courses" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <BookOpen className="h-4 w-4 mr-2" /> All Courses
          </TabsTrigger>
          <TabsTrigger value="builder" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Layers className="h-4 w-4 mr-2" /> Course Builder
          </TabsTrigger>
        </TabsList>

        {/* All Courses Tab */}
        <TabsContent value="courses" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-800">Manage Courses</h3>
            <Dialog open={courseDialogOpen} onOpenChange={(open) => { setCourseDialogOpen(open); if (!open) resetCourseForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" /> Create Course
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCourse ? 'Edit Course' : 'Create New Course'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCourse} className="space-y-4">
                  <div>
                    <Label>Course Title *</Label>
                    <Input value={courseForm.title} onChange={(e) => setCourseForm({...courseForm, title: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <textarea className="w-full p-2 border rounded-lg min-h-[80px]" value={courseForm.description} onChange={(e) => setCourseForm({...courseForm, description: e.target.value})} />
                  </div>
                  <div>
                    <Label>Thumbnail URL</Label>
                    <Input value={courseForm.thumbnail_url} onChange={(e) => setCourseForm({...courseForm, thumbnail_url: e.target.value})} placeholder="https://..." />
                  </div>
                  <div>
                    <Label>Instructor Name</Label>
                    <Input value={courseForm.instructor_name} onChange={(e) => setCourseForm({...courseForm, instructor_name: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category</Label>
                      <select className="w-full p-2 border rounded-lg" value={courseForm.category} onChange={(e) => setCourseForm({...courseForm, category: e.target.value})}>
                        <option>General</option>
                        <option>IT & Software</option>
                        <option>Design</option>
                        <option>Business</option>
                        <option>Finance</option>
                        <option>Language</option>
                      </select>
                    </div>
                    <div>
                      <Label>Tags (comma separated)</Label>
                      <Input value={courseForm.tags} onChange={(e) => setCourseForm({...courseForm, tags: e.target.value})} placeholder="autocad, design" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="is_free" checked={courseForm.is_free} onChange={(e) => setCourseForm({...courseForm, is_free: e.target.checked})} />
                    <Label htmlFor="is_free">Free Course</Label>
                  </div>
                  {!courseForm.is_free && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Price (₹)</Label>
                        <Input type="number" value={courseForm.price} onChange={(e) => setCourseForm({...courseForm, price: e.target.value})} />
                      </div>
                      <div>
                        <Label>Discount Price (₹)</Label>
                        <Input type="number" value={courseForm.discount_price} onChange={(e) => setCourseForm({...courseForm, discount_price: e.target.value})} />
                      </div>
                    </div>
                  )}
                  <Button type="submit" className="w-full">{editingCourse ? 'Update' : 'Create'} Course</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(course => (
              <div key={course.id} className="bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video bg-slate-100 relative">
                  {course.thumbnail_url ? (
                    <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-12 w-12 text-slate-300" />
                    </div>
                  )}
                  <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium ${course.is_published ? 'bg-emerald-500 text-white' : 'bg-slate-500 text-white'}`}>
                    {course.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <div className="p-4">
                  <h4 className="font-bold text-slate-800 line-clamp-1">{course.title}</h4>
                  <p className="text-sm text-slate-500 mt-1">{course.instructor_name}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><FileVideo className="h-3 w-3" /> {course.total_lessons || 0} lessons</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDuration(course.total_duration || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-bold text-purple-600">
                      {course.is_free ? 'Free' : `₹${course.discount_price || course.price}`}
                      {course.discount_price && <span className="text-xs text-slate-400 line-through ml-1">₹{course.price}</span>}
                    </span>
                    <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{course.category}</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => { setSelectedCourse(course); setActiveTab('builder'); }}>
                      <Layers className="h-3 w-3 mr-1" /> Build
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEditCourse(course)}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant={course.is_published ? "outline" : "default"} onClick={() => togglePublish(course.id)}>
                      {course.is_published ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteCourse(course.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {courses.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-400">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No courses created yet. Create your first course!</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Course Builder Tab */}
        <TabsContent value="builder" className="space-y-4">
          {!selectedCourse ? (
            <div className="text-center py-12 bg-white rounded-xl border">
              <Layers className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Select a course from "All Courses" tab to start building</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-slate-800">{selectedCourse.title}</h3>
                  <p className="text-sm text-slate-500">{selectedCourse.total_lessons || 0} lessons • {formatDuration(selectedCourse.total_duration || 0)}</p>
                </div>
                <Dialog open={moduleDialogOpen} onOpenChange={setModuleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="h-4 w-4 mr-2" /> Add Module
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Module</DialogTitle></DialogHeader>
                    <form onSubmit={handleAddModule} className="space-y-4">
                      <div>
                        <Label>Module Title *</Label>
                        <Input value={moduleForm.title} onChange={(e) => setModuleForm({...moduleForm, title: e.target.value})} required />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input value={moduleForm.description} onChange={(e) => setModuleForm({...moduleForm, description: e.target.value})} />
                      </div>
                      <div>
                        <Label>Order</Label>
                        <Input type="number" value={moduleForm.order} onChange={(e) => setModuleForm({...moduleForm, order: parseInt(e.target.value)})} />
                      </div>
                      <Button type="submit" className="w-full">Add Module</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Modules List */}
              <div className="space-y-4">
                {(selectedCourse.modules || []).sort((a,b) => a.order - b.order).map((module, mIdx) => (
                  <div key={module.id} className="border rounded-lg">
                    <div 
                      className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer"
                      onClick={() => setExpandedModules({...expandedModules, [module.id]: !expandedModules[module.id]})}
                    >
                      <div className="flex items-center gap-3">
                        {expandedModules[module.id] ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        <FolderOpen className="h-5 w-5 text-purple-600" />
                        <span className="font-medium">Module {mIdx + 1}: {module.title}</span>
                        <span className="text-xs text-slate-400">({(module.chapters || []).length} chapters)</span>
                      </div>
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="outline" onClick={() => openChapterDialog(module.id)}>
                          <Plus className="h-3 w-3 mr-1" /> Chapter
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteModule(module.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {expandedModules[module.id] && (
                      <div className="p-4 space-y-3">
                        {(module.chapters || []).sort((a,b) => a.order - b.order).map((chapter, cIdx) => (
                          <div key={chapter.id} className="border rounded-lg ml-4">
                            <div 
                              className="flex items-center justify-between p-3 bg-blue-50 cursor-pointer"
                              onClick={() => setExpandedChapters({...expandedChapters, [chapter.id]: !expandedChapters[chapter.id]})}
                            >
                              <div className="flex items-center gap-2">
                                {expandedChapters[chapter.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                <span className="text-sm font-medium">Chapter {cIdx + 1}: {chapter.title}</span>
                                <span className="text-xs text-slate-400">({(chapter.lessons || []).length} lessons)</span>
                              </div>
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openLessonDialog(module.id, chapter.id)}>
                                  <Plus className="h-3 w-3 mr-1" /> Lesson
                                </Button>
                              </div>
                            </div>
                            
                            {expandedChapters[chapter.id] && (
                              <div className="p-3 space-y-2">
                                {(chapter.lessons || []).sort((a,b) => a.order - b.order).map((lesson, lIdx) => (
                                  <div key={lesson.id} className="flex items-center justify-between p-2 bg-white border rounded ml-4">
                                    <div className="flex items-center gap-2">
                                      <PlayCircle className="h-4 w-4 text-emerald-600" />
                                      <span className="text-sm">{lIdx + 1}. {lesson.title}</span>
                                      {lesson.is_preview && <span className="text-xs bg-emerald-100 text-emerald-700 px-1 rounded">Preview</span>}
                                      {lesson.has_quiz && <span className="text-xs bg-amber-100 text-amber-700 px-1 rounded">Quiz</span>}
                                      <span className="text-xs text-slate-400">{formatDuration(lesson.video_duration || 0)}</span>
                                    </div>
                                    <div className="flex gap-1">
                                      {!lesson.has_quiz && (
                                        <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => openQuizDialog(lesson.id)}>
                                          <HelpCircle className="h-3 w-3" />
                                        </Button>
                                      )}
                                      <Button size="sm" variant="destructive" className="h-6" onClick={() => deleteLesson(lesson.id)}>
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                                {(chapter.lessons || []).length === 0 && (
                                  <p className="text-xs text-slate-400 ml-4">No lessons yet. Add your first lesson!</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        {(module.chapters || []).length === 0 && (
                          <p className="text-sm text-slate-400 ml-4">No chapters yet. Add your first chapter!</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {(selectedCourse.modules || []).length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No modules yet. Start by adding a module!</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chapter Dialog */}
          <Dialog open={chapterDialogOpen} onOpenChange={setChapterDialogOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Chapter</DialogTitle></DialogHeader>
              <form onSubmit={handleAddChapter} className="space-y-4">
                <div>
                  <Label>Chapter Title *</Label>
                  <Input value={chapterForm.title} onChange={(e) => setChapterForm({...chapterForm, title: e.target.value})} required />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={chapterForm.description} onChange={(e) => setChapterForm({...chapterForm, description: e.target.value})} />
                </div>
                <Button type="submit" className="w-full">Add Chapter</Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Lesson Dialog */}
          <Dialog open={lessonDialogOpen} onOpenChange={setLessonDialogOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add Lesson</DialogTitle></DialogHeader>
              <form onSubmit={handleAddLesson} className="space-y-4">
                <div>
                  <Label>Lesson Title *</Label>
                  <Input value={lessonForm.title} onChange={(e) => setLessonForm({...lessonForm, title: e.target.value})} required />
                </div>
                <div>
                  <Label>Description</Label>
                  <textarea className="w-full p-2 border rounded-lg" value={lessonForm.description} onChange={(e) => setLessonForm({...lessonForm, description: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Video Type</Label>
                    <select className="w-full p-2 border rounded-lg" value={lessonForm.video_type} onChange={(e) => setLessonForm({...lessonForm, video_type: e.target.value})}>
                      <option value="youtube">YouTube</option>
                      <option value="upload">Direct Upload</option>
                    </select>
                  </div>
                  <div>
                    <Label>Duration (seconds)</Label>
                    <Input type="number" value={lessonForm.video_duration} onChange={(e) => setLessonForm({...lessonForm, video_duration: e.target.value})} placeholder="600" />
                  </div>
                </div>
                <div>
                  <Label>Video URL</Label>
                  <Input value={lessonForm.video_url} onChange={(e) => setLessonForm({...lessonForm, video_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." />
                </div>
                <div>
                  <Label>Notes (Rich Text)</Label>
                  <textarea className="w-full p-2 border rounded-lg min-h-[100px]" value={lessonForm.notes_text} onChange={(e) => setLessonForm({...lessonForm, notes_text: e.target.value})} placeholder="Lesson notes in text format..." />
                </div>
                <div>
                  <Label>Notes PDF URL</Label>
                  <Input value={lessonForm.notes_pdf_url} onChange={(e) => setLessonForm({...lessonForm, notes_pdf_url: e.target.value})} placeholder="https://..." />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="is_preview" checked={lessonForm.is_preview} onChange={(e) => setLessonForm({...lessonForm, is_preview: e.target.checked})} />
                  <Label htmlFor="is_preview">Free Preview (available without enrollment)</Label>
                </div>
                <Button type="submit" className="w-full">Add Lesson</Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Quiz Dialog */}
          <Dialog open={quizDialogOpen} onOpenChange={setQuizDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add Quiz</DialogTitle></DialogHeader>
              <form onSubmit={handleAddQuiz} className="space-y-4">
                <div>
                  <Label>Passing Score (%)</Label>
                  <Input type="number" value={quizForm.passing_score} onChange={(e) => setQuizForm({...quizForm, passing_score: parseInt(e.target.value)})} />
                </div>
                
                {quizForm.questions.map((q, qIdx) => (
                  <div key={qIdx} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Question {qIdx + 1}</span>
                      <Input type="number" className="w-20" value={q.points} onChange={(e) => updateQuestion(qIdx, 'points', parseInt(e.target.value))} placeholder="Points" />
                    </div>
                    <Input value={q.question} onChange={(e) => updateQuestion(qIdx, 'question', e.target.value)} placeholder="Enter question..." />
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <div key={oIdx} className="flex items-center gap-2">
                          <input type="radio" name={`correct_${qIdx}`} checked={q.correct_answer === oIdx} onChange={() => updateQuestion(qIdx, 'correct_answer', oIdx)} />
                          <Input value={opt} onChange={(e) => updateOption(qIdx, oIdx, e.target.value)} placeholder={`Option ${oIdx + 1}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <Button type="button" variant="outline" onClick={addQuizQuestion} className="w-full">
                  <Plus className="h-4 w-4 mr-2" /> Add Question
                </Button>
                <Button type="submit" className="w-full">Save Quiz</Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LMSManagement;
